import { useEffect, useMemo, useState, Fragment } from 'react';
import SettingsPageShell from './SettingsPageShell';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Receipt, Wallet, TrendingDown, ShieldAlert, CheckCircle,
  ChevronDown, ChevronRight, Info, MessageSquare,
} from 'lucide-react';
import { retailer, getErrorMessage } from '../../utils/api';
import EvidenceViewer from '../shared/EvidenceViewer';
import DisputeSubmitModal from './DisputeSubmitModal';

const DISPUTE_WINDOW_DAYS = 7;

const _fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
};

const _daysLeft = (createdAt) => {
  if (!createdAt) return null;
  const ms = new Date(createdAt).getTime() + DISPUTE_WINDOW_DAYS * 24 * 60 * 60 * 1000 - Date.now();
  if (ms <= 0) return 0;
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
};

const _reasonBadge = (reason) => {
  const map = {
    spoiled: 'bg-red-100 text-red-800',
    expired: 'bg-red-100 text-red-800',
    damaged: 'bg-orange-100 text-orange-800',
    mislabeled: 'bg-orange-100 text-orange-800',
    recall: 'bg-red-100 text-red-800',
    missing_item_retailer_fault: 'bg-orange-100 text-orange-800',
    missing_item_courier_fault: 'bg-blue-100 text-blue-800',
    delivery_damage: 'bg-blue-100 text-blue-800',
    entire_order_undelivered: 'bg-blue-100 text-blue-800',
    customer_changed_mind: 'bg-gray-100 text-gray-800',
    fraudulent_claim: 'bg-gray-100 text-gray-800',
    other: 'bg-gray-100 text-gray-800',
  };
  return (
    <Badge className={map[reason] || 'bg-gray-100 text-gray-800'}>
      {reason.replace(/_/g, ' ')}
    </Badge>
  );
};

const _statusBadge = (status) => {
  const map = {
    applied: 'bg-amber-100 text-amber-800',
    disputed: 'bg-purple-100 text-purple-800',
    resolved: 'bg-emerald-100 text-emerald-800',
    reversed: 'bg-emerald-100 text-emerald-800',
  };
  return <Badge className={map[status] || 'bg-gray-100 text-gray-800'}>{status}</Badge>;
};

export default function RetailerSettlementDashboard({ user, onLogout }) {
  const [loading, setLoading] = useState(true);
  const [settlements, setSettlements] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [disputeTarget, setDisputeTarget] = useState(null);

  const _load = async () => {
    try {
      setLoading(true);
      const [s, a, d] = await Promise.all([
        retailer.listSettlements(),
        retailer.listRefunds(),
        retailer.listDisputes(),
      ]);
      setSettlements(s.data?.settlements || []);
      setAdjustments(a.data?.adjustments || []);
      setDisputes(d.data?.disputes || []);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load settlement data'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { _load(); }, []);

  const currentSettlement = settlements[0]; // sorted period_start desc by backend
  const disputesByAdj = useMemo(() => {
    const m = {};
    disputes.forEach((d) => { m[d.adjustment_id] = d; });
    return m;
  }, [disputes]);

  const unlinkedAdjustments = useMemo(
    () => adjustments.filter((a) => !a.settlement_id),
    [adjustments]
  );

  const _canDispute = (adj) => {
    if (!adj.settlement_id) return false;
    if (disputesByAdj[adj.id]) return false;
    if (adj.status === 'resolved' || adj.status === 'reversed') return false;
    const s = settlements.find((x) => x.id === adj.settlement_id);
    if (!s) return false;
    return _daysLeft(s.created_at) > 0;
  };

  const _openDispute = (adj) => setDisputeTarget(adj);
  const _closeDispute = () => setDisputeTarget(null);

  const _adjustmentsForSettlement = (settlementId) =>
    adjustments.filter((a) => a.settlement_id === settlementId);

  const _renderAdjustmentRow = (adj) => {
    const existingDispute = disputesByAdj[adj.id];
    return (
      <TableRow key={adj.id} data-testid={`adj-row-${adj.id}`}>
        <TableCell className="text-xs font-mono">{adj.id.slice(0, 8)}</TableCell>
        <TableCell>{_reasonBadge(adj.reason)}</TableCell>
        <TableCell className="capitalize">{adj.fault}</TableCell>
        <TableCell className="text-right font-medium">
          ${Number(adj.withheld_amount || 0).toFixed(2)}
        </TableCell>
        <TableCell>{_statusBadge(adj.status)}</TableCell>
        <TableCell>{_fmtDate(adj.created_at)}</TableCell>
        <TableCell>
          {existingDispute ? (
            <Badge className="bg-purple-100 text-purple-800">
              {existingDispute.resolution || 'Under review'}
            </Badge>
          ) : _canDispute(adj) ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => _openDispute(adj)}
              data-testid={`dispute-btn-${adj.id}`}
            >
              <MessageSquare className="w-3 h-3 mr-1" />
              Dispute
            </Button>
          ) : (
            <span className="text-xs text-gray-400">—</span>
          )}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <SettingsPageShell
      user={user}
      onLogout={onLogout}
      title="Settlements"
      subtitle="Monthly rollup statements, refund adjustments & disputes"
    >
      <div className="space-y-6" data-testid="retailer-settlements-page">

        {/* Wallet snapshot */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="bg-emerald-100 p-3 rounded-lg">
                  <Wallet className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Current Month Net</p>
                  <p className="text-2xl font-bold text-emerald-600" data-testid="wallet-net">
                    ${loading ? '…' : Number(currentSettlement?.net_payable || 0).toFixed(2)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="bg-red-100 p-3 rounded-lg">
                  <TrendingDown className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Adjustments This Month</p>
                  <p className="text-2xl font-bold text-red-600" data-testid="wallet-adjustments">
                    -${loading ? '…' : Number(currentSettlement?.total_adjustments || 0).toFixed(2)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="bg-amber-100 p-3 rounded-lg">
                  <ShieldAlert className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Pending (Not Yet Rolled Up)</p>
                  <p className="text-2xl font-bold text-amber-600" data-testid="wallet-pending">
                    {loading ? '…' : unlinkedAdjustments.length} item{unlinkedAdjustments.length === 1 ? '' : 's'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Dispute-window explainer */}
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 flex gap-2 text-sm text-blue-900" data-testid="dispute-window-explainer">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            DealShaq issues a monthly rollup statement on the 1st of each month. You have
            <strong> {DISPUTE_WINDOW_DAYS} calendar days </strong>from the statement date to
            file a dispute. Refer to Retailer Agreement §3.7.
          </div>
        </div>

        {/* Monthly rollup statements */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-emerald-600" />
              Monthly Rollup Statements
            </CardTitle>
            <CardDescription>Weekly payouts consolidated month by month</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-gray-500">Loading…</p>
            ) : settlements.length === 0 ? (
              <p className="text-sm text-gray-500" data-testid="no-settlements">
                No rollups yet. You'll see one here on the 1st of the month.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8" />
                      <TableHead>Period</TableHead>
                      <TableHead className="text-right">Gross Wholesale</TableHead>
                      <TableHead className="text-right">Adjustments</TableHead>
                      <TableHead className="text-right">Net Payable</TableHead>
                      <TableHead>Dispute Window</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {settlements.map((s) => {
                      const days = _daysLeft(s.created_at);
                      const isExpanded = expanded === s.id;
                      return (
                        <Fragment key={s.id}>
                          <TableRow
                            className="cursor-pointer hover:bg-gray-50"
                            onClick={() => setExpanded(isExpanded ? null : s.id)}
                            data-testid={`settlement-row-${s.id}`}
                          >
                            <TableCell>
                              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </TableCell>
                            <TableCell>
                              <div className="text-sm font-medium">
                                {_fmtDate(s.period_start)} → {_fmtDate(s.period_end)}
                              </div>
                              <div className="text-xs text-gray-500">
                                Issued {_fmtDate(s.created_at)}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              ${Number(s.gross_wholesale_value || 0).toFixed(2)}
                            </TableCell>
                            <TableCell className="text-right text-red-600">
                              -${Number(s.total_adjustments || 0).toFixed(2)}
                            </TableCell>
                            <TableCell className="text-right font-bold text-emerald-700">
                              ${Number(s.net_payable || 0).toFixed(2)}
                            </TableCell>
                            <TableCell>
                              {days > 0 ? (
                                <Badge className="bg-amber-100 text-amber-800">
                                  {days} day{days === 1 ? '' : 's'} left
                                </Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-700">Closed</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge className="bg-slate-100 text-slate-800">{s.status}</Badge>
                            </TableCell>
                          </TableRow>
                          {isExpanded && (
                            <TableRow>
                              <TableCell colSpan={7} className="bg-gray-50">
                                <div className="p-3 space-y-3">
                                  <h4 className="text-sm font-semibold text-gray-700">
                                    Refund adjustments in this statement
                                  </h4>
                                  {_adjustmentsForSettlement(s.id).length === 0 ? (
                                    <p className="text-xs text-gray-500">
                                      No adjustments in this period.
                                    </p>
                                  ) : (
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead>ID</TableHead>
                                          <TableHead>Reason</TableHead>
                                          <TableHead>Fault</TableHead>
                                          <TableHead className="text-right">Withheld</TableHead>
                                          <TableHead>Status</TableHead>
                                          <TableHead>Date</TableHead>
                                          <TableHead>Action</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {_adjustmentsForSettlement(s.id).map(_renderAdjustmentRow)}
                                      </TableBody>
                                    </Table>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending (unlinked) adjustments */}
        {unlinkedAdjustments.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-amber-600" />
                Pending Adjustments
              </CardTitle>
              <CardDescription>
                These adjustments will appear on your next monthly rollup and open the 7-day dispute window at that time.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Fault</TableHead>
                    <TableHead className="text-right">Withheld</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>{unlinkedAdjustments.map(_renderAdjustmentRow)}</TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* My disputes */}
        {disputes.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-purple-600" />
                My Disputes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {disputes.map((d) => (
                <div
                  key={d.id}
                  className="border rounded-md p-3 space-y-2"
                  data-testid={`dispute-card-${d.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-xs font-mono text-gray-500">
                        Dispute #{d.id.slice(0, 8)}
                      </div>
                      <div className="text-sm">{d.retailer_comment}</div>
                    </div>
                    <div>
                      {d.resolution === 'approved' ? (
                        <Badge className="bg-emerald-100 text-emerald-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Approved
                        </Badge>
                      ) : d.resolution === 'denied' ? (
                        <Badge className="bg-red-100 text-red-800">Denied</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800">Under review</Badge>
                      )}
                    </div>
                  </div>
                  {(d.retailer_evidence_urls?.length ?? 0) > 0 && (
                    <EvidenceViewer urls={d.retailer_evidence_urls} title="My evidence" />
                  )}
                  {d.admin_notes && (
                    <div className="text-xs text-gray-600 border-l-2 border-gray-300 pl-2">
                      Admin: {d.admin_notes}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <DisputeSubmitModal
          open={Boolean(disputeTarget)}
          onOpenChange={(v) => (v ? null : _closeDispute())}
          adjustment={disputeTarget}
          onFiled={_load}
        />
      </div>
    </SettingsPageShell>
  );
}
